import React, { Fragment } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, BarChart2Icon, CodeIcon, DatabaseIcon, LineChartIcon, TrendingUpIcon } from 'lucide-react';
import { Button } from '../../components/Button';
import { MetricCard } from '../../components/MetricCard';
import { SectionHeading } from '../../components/SectionHeading';
export function PlatformCaseStudy() {
  return <div className="bg-cream" data-id="element-566">
      {/* Hero Section */}
      <section className="py-16 bg-white" data-id="element-567">
        <div className="container mx-auto px-6" data-id="element-568">
          <div className="max-w-4xl mx-auto" data-id="element-569">
            <h1 className="text-3xl md:text-4xl font-display font-bold text-navy-dark mb-6" data-id="element-570">
              Building the FFF Platform
            </h1>
            <p className="text-lg text-navy mb-8" data-id="element-571">
              Transforming a nutrition company from spreadsheet-driven
              operations to a fully integrated digital platform.
            </p>
            <div className="mb-12" data-id="element-572">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" alt="FFF Platform" className="w-full h-auto rounded-lg shadow-lg" data-id="element-573" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-574">
              <MetricCard value="14%" label="Gross Margin Improvement" data-id="element-575" />
              <MetricCard value="100%" label="Manual Processes Eliminated" data-id="element-576" />
              <MetricCard value="UK-wide" label="Expansion Enabled" data-id="element-577" />
            </div>
          </div>
        </div>
      </section>
      {/* Challenge Section */}
      <section className="py-16 bg-cream" data-id="element-578">
        <div className="container mx-auto px-6" data-id="element-579">
          <div className="max-w-4xl mx-auto" data-id="element-580">
            <SectionHeading title="The Challenge" subtitle="A growing nutrition company constrained by manual processes" data-id="element-581" />
            <div className="bg-white rounded-lg shadow-sm p-8 mb-12" data-id="element-582">
              <p className="text-navy mb-6" data-id="element-583">
                When I joined Fresh Fitness Food, the company was running
                entirely on Excel spreadsheets. Every aspect of the business -
                from customer meal plans to kitchen production to delivery
                logistics - was managed manually, creating significant
                challenges:
              </p>
              <div className="space-y-6" data-id="element-584">
                <div className="flex" data-id="element-585">
                  <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-586">
                    <TrendingUpIcon size={20} data-id="element-587" />
                  </div>
                  <div data-id="element-588">
                    <h3 className="text-lg font-display font-semibold text-navy-dark mb-1" data-id="element-589">
                      Scaling Limitations
                    </h3>
                    <p className="text-navy" data-id="element-590">
                      Growth required proportional headcount increases, as
                      manual processes couldn't scale efficiently. This created
                      a ceiling on how large the business could become.
                    </p>
                  </div>
                </div>
                <div className="flex" data-id="element-591">
                  <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-592">
                    <BarChart2Icon size={20} data-id="element-593" />
                  </div>
                  <div data-id="element-594">
                    <h3 className="text-lg font-display font-semibold text-navy-dark mb-1" data-id="element-595">
                      Margin Pressure
                    </h3>
                    <p className="text-navy" data-id="element-596">
                      Manual processes were inefficient and error-prone, leading
                      to food waste, inaccurate ingredient forecasting, and high
                      operational costs.
                    </p>
                  </div>
                </div>
                <div className="flex" data-id="element-597">
                  <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-598">
                    <DatabaseIcon size={20} data-id="element-599" />
                  </div>
                  <div data-id="element-600">
                    <h3 className="text-lg font-display font-semibold text-navy-dark mb-1" data-id="element-601">
                      Data Fragmentation
                    </h3>
                    <p className="text-navy" data-id="element-602">
                      Customer data, nutrition information, and operational data
                      existed in separate systems with no integration, making it
                      impossible to gain insights or optimize the business.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Solution Section */}
      <section className="py-16 bg-white" data-id="element-603">
        <div className="container mx-auto px-6" data-id="element-604">
          <div className="max-w-4xl mx-auto" data-id="element-605">
            <SectionHeading title="The Solution" subtitle="Building an end-to-end ERP system for a nutrition company" data-id="element-606" />
            <div className="space-y-12" data-id="element-607">
              <div data-id="element-608">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-4" data-id="element-609">
                  My Approach
                </h3>
                <p className="text-navy mb-6" data-id="element-610">
                  I led the design and development of a proprietary platform
                  that would connect every part of the business - from customer
                  ordering to meal planning to kitchen production to delivery
                  logistics.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-611">
                  <div className="bg-cream rounded-lg p-6" data-id="element-612">
                    <h4 className="text-lg font-medium text-navy-dark mb-3" data-id="element-613">
                      Phase 1: Core Infrastructure
                    </h4>
                    <ul className="space-y-2" data-id="element-614">
                      <li className="flex" data-id="element-615">
                        <span className="text-coral-dark mr-3" data-id="element-616">•</span>
                        <span className="text-navy" data-id="element-617">
                          Customer database with nutrition profiles
                        </span>
                      </li>
                      <li className="flex" data-id="element-618">
                        <span className="text-coral-dark mr-3" data-id="element-619">•</span>
                        <span className="text-navy" data-id="element-620">
                          Recipe database with full nutritional breakdown
                        </span>
                      </li>
                      <li className="flex" data-id="element-621">
                        <span className="text-coral-dark mr-3" data-id="element-622">•</span>
                        <span className="text-navy" data-id="element-623">
                          Menu planning and rotation system
                        </span>
                      </li>
                      <li className="flex" data-id="element-624">
                        <span className="text-coral-dark mr-3" data-id="element-625">•</span>
                        <span className="text-navy" data-id="element-626">
                          Ingredient database with supplier information
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-cream rounded-lg p-6" data-id="element-627">
                    <h4 className="text-lg font-medium text-navy-dark mb-3" data-id="element-628">
                      Phase 2: Operational Automation
                    </h4>
                    <ul className="space-y-2" data-id="element-629">
                      <li className="flex" data-id="element-630">
                        <span className="text-coral-dark mr-3" data-id="element-631">•</span>
                        <span className="text-navy" data-id="element-632">
                          Kitchen production workflow automation
                        </span>
                      </li>
                      <li className="flex" data-id="element-633">
                        <span className="text-coral-dark mr-3" data-id="element-634">•</span>
                        <span className="text-navy" data-id="element-635">
                          Ingredient forecasting and ordering
                        </span>
                      </li>
                      <li className="flex" data-id="element-636">
                        <span className="text-coral-dark mr-3" data-id="element-637">•</span>
                        <span className="text-navy" data-id="element-638">
                          Delivery route optimization
                        </span>
                      </li>
                      <li className="flex" data-id="element-639">
                        <span className="text-coral-dark mr-3" data-id="element-640">•</span>
                        <span className="text-navy" data-id="element-641">
                          Quality control and compliance tracking
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div data-id="element-642">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-4" data-id="element-643">
                  Key Technical Features
                </h3>
                <div className="space-y-6" data-id="element-644">
                  <div className="flex" data-id="element-645">
                    <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-646">
                      <CodeIcon size={20} data-id="element-647" />
                    </div>
                    <div data-id="element-648">
                      <h4 className="text-lg font-medium text-navy-dark mb-1" data-id="element-649">
                        Nutrition Algorithm Engine
                      </h4>
                      <p className="text-navy" data-id="element-650">
                        I designed a sophisticated algorithm that could generate
                        personalized meal plans based on individual customer
                        profiles, dietary preferences, and nutritional goals.
                        This engine could automatically adjust macronutrient
                        distributions and portion sizes while ensuring menu
                        variety.
                      </p>
                    </div>
                  </div>
                  <div className="flex" data-id="element-651">
                    <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-652">
                      <LineChartIcon size={20} data-id="element-653" />
                    </div>
                    <div data-id="element-654">
                      <h4 className="text-lg font-medium text-navy-dark mb-1" data-id="element-655">
                        Production Workflow System
                      </h4>
                      <p className="text-navy" data-id="element-656">
                        I developed a system that automatically generated
                        kitchen production documents, prep lists, and packaging
                        instructions based on the day's orders. This eliminated
                        manual transcription and calculation errors while
                        providing real-time updates as orders changed.
                      </p>
                    </div>
                  </div>
                  <div className="flex" data-id="element-657">
                    <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-658">
                      <DatabaseIcon size={20} data-id="element-659" />
                    </div>
                    <div data-id="element-660">
                      <h4 className="text-lg font-medium text-navy-dark mb-1" data-id="element-661">
                        Integrated Data Platform
                      </h4>
                      <p className="text-navy" data-id="element-662">
                        I created a centralized data platform that connected
                        customer information, nutrition data, production
                        metrics, and delivery logistics. This enabled powerful
                        reporting and analytics that drove business decisions
                        and continuous improvement.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-663">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-4" data-id="element-664">
                  How My Domain Expertise Made a Difference
                </h3>
                <p className="text-navy mb-6" data-id="element-665">
                  My background in nutrition science was crucial to the success
                  of this project. I was able to:
                </p>
                <ul className="space-y-3" data-id="element-666">
                  <li className="flex" data-id="element-667">
                    <span className="text-coral-dark mr-3" data-id="element-668">•</span>
                    <span className="text-navy" data-id="element-669">
                      Design nutrition algorithms that were scientifically sound
                      while meeting business needs
                    </span>
                  </li>
                  <li className="flex" data-id="element-670">
                    <span className="text-coral-dark mr-3" data-id="element-671">•</span>
                    <span className="text-navy" data-id="element-672">
                      Translate complex nutritional requirements into technical
                      specifications
                    </span>
                  </li>
                  <li className="flex" data-id="element-673">
                    <span className="text-coral-dark mr-3" data-id="element-674">•</span>
                    <span className="text-navy" data-id="element-675">
                      Bridge the gap between culinary operations and technology
                    </span>
                  </li>
                  <li className="flex" data-id="element-676">
                    <span className="text-coral-dark mr-3" data-id="element-677">•</span>
                    <span className="text-navy" data-id="element-678">
                      Ensure the platform supported genuine nutritional
                      personalisation, not just superficial customisation
                    </span>
                  </li>
                  <li className="flex" data-id="element-679">
                    <span className="text-coral-dark mr-3" data-id="element-680">•</span>
                    <span className="text-navy" data-id="element-681">
                      Create systems that made quality nutrition more accessible
                      and affordable
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Results Section */}
      <section className="py-16 bg-cream" data-id="element-682">
        <div className="container mx-auto px-6" data-id="element-683">
          <div className="max-w-4xl mx-auto" data-id="element-684">
            <SectionHeading title="The Outcome" subtitle="Transformational business impact through digital transformation" data-id="element-685" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-686">
              <MetricCard value="14%" label="Gross Margin Improvement" className="bg-sage-light" data-id="element-687" />
              <MetricCard value="3x" label="Operational Capacity Increase" className="bg-sage-light" data-id="element-688" />
              <MetricCard value="UK-wide" label="Geographic Expansion" className="bg-sage-light" data-id="element-689" />
            </div>
            <div className="bg-white rounded-lg shadow-sm p-8" data-id="element-690">
              <div className="space-y-6" data-id="element-691">
                <div data-id="element-692">
                  <h3 className="text-lg font-display font-semibold text-navy-dark mb-2" data-id="element-693">
                    Financial Impact
                  </h3>
                  <p className="text-navy" data-id="element-694">
                    The platform directly improved gross margins by 14% through
                    reduced food waste, optimized ingredient purchasing,
                    streamlined production, and more efficient delivery routing.
                    This transformed the unit economics of the business.
                  </p>
                </div>
                <div data-id="element-695">
                  <h3 className="text-lg font-display font-semibold text-navy-dark mb-2" data-id="element-696">
                    Operational Transformation
                  </h3>
                  <p className="text-navy" data-id="element-697">
                    We eliminated 100% of manual Excel-based processes, reducing
                    errors and allowing the team to focus on value-adding
                    activities rather than data entry and calculations. The
                    kitchen could produce three times as many meals with the
                    same staff.
                  </p>
                </div>
                <div data-id="element-698">
                  <h3 className="text-lg font-display font-semibold text-navy-dark mb-2" data-id="element-699">
                    Business Expansion
                  </h3>
                  <p className="text-navy" data-id="element-700">
                    The platform enabled nationwide expansion without
                    proportional headcount growth. What was once a London-only
                    service could now operate across the UK with centralized
                    production and optimized logistics.
                  </p>
                </div>
                <div data-id="element-701">
                  <h3 className="text-lg font-display font-semibold text-navy-dark mb-2" data-id="element-702">
                    Product Innovation
                  </h3>
                  <p className="text-navy" data-id="element-703">
                    With the platform in place, we could rapidly develop and
                    launch new product offerings, including fully customizable
                    meal plans, supplement ranges, and frozen options. This
                    diversified revenue streams and increased customer lifetime
                    value.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Navigation Section */}
      <section className="py-12 bg-white" data-id="element-704">
        <div className="container mx-auto px-6" data-id="element-705">
          <div className="max-w-4xl mx-auto flex justify-between" data-id="element-706">
            <Button href="/" variant="outline" data-id="element-707">
              <ArrowLeftIcon size={16} className="mr-2" data-id="element-708" />
              Back to Home
            </Button>
            <Button href="/case-study/acquisition" data-id="element-709">
              Next Case Study
              <ArrowRightIcon size={16} className="ml-2" data-id="element-710" />
            </Button>
          </div>
        </div>
      </section>
    </div>;
}